import type {
  AddressData,
  AppFieldResponse,
  CheckoutData,
  PersonalInfo,
  SelectedService,
  WaterPlan,
} from '@/types';
import type { ConfiguredPlan } from '@/stores/orderStore';
import { validateDOB } from '@/utils/dateValidation';
import {
  getAllowedDays,
  getEarliestServiceDate,
  getLeadTime,
  getScheduleDiagnostics,
  isServiceDateValid,
} from '@/utils/scheduling';

export interface CheckoutAssemblyInput {
  address: AddressData;
  personalInfo: PersonalInfo;
  configuredPlans: ConfiguredPlan[];
  selectedServices: SelectedService[];
  appFields: AppFieldResponse[];
  onitPlan: WaterPlan | null;
  onitSelected: boolean;
  requireAppFields?: boolean;
}

export interface CheckoutAssemblySuccess {
  ok: true;
  checkoutData: CheckoutData;
  serviceStartDateSelection: Record<string, string>;
  warnings: string[];
}

export interface CheckoutAssemblyFailure {
  ok: false;
  error: string;
  logMessage: string;
}

export type CheckoutAssemblyResult = CheckoutAssemblySuccess | CheckoutAssemblyFailure;

export function formatSignedDate(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  };
  return date.toLocaleString('en-US', options);
}

export function buildServiceStartDateSelection(
  configuredPlans: ConfiguredPlan[],
  selectedServices: SelectedService[],
  onitPlan: WaterPlan | null,
  onitSelected: boolean
): {
  serviceStartDateSelection: Record<string, string>;
  missingServiceNames: string[];
  invalidServiceNames: string[];
  invalidServiceDetails: Array<{
    name: string;
    type: string;
    planId: string;
    vendorId: string;
    serviceDate?: string;
    minDate: string;
    allowedDays: boolean[];
    scheduleDiagnostics: ReturnType<typeof getScheduleDiagnostics>;
  }>;
  warnings: string[];
} {
  const serviceStartDateSelection: Record<string, string> = {};
  const missingServiceNames: string[] = [];
  const invalidServiceNames: string[] = [];
  const invalidServiceDetails: Array<{
    name: string;
    type: string;
    planId: string;
    vendorId: string;
    serviceDate?: string;
    minDate: string;
    allowedDays: boolean[];
    scheduleDiagnostics: ReturnType<typeof getScheduleDiagnostics>;
  }> = [];
  const warnings: string[] = [];

  selectedServices.forEach((service) => {
    const configuredPlan = configuredPlans.find((plan) => plan.planId === service.planId);
    const serviceDate = configuredPlan?.serviceDate;
    const serviceName = service.name || service.vendorName || service.type;
    const scheduleDiagnostics = getScheduleDiagnostics(service);

    if (scheduleDiagnostics.missingWeekendValues.length > 0) {
      warnings.push(
        `Schedule weekend fields missing for ${serviceName}: ${scheduleDiagnostics.missingWeekendValues.join(', ')}.`
      );
    }

    if (!serviceDate) {
      missingServiceNames.push(serviceName);
      return;
    }

    const allowedDays = getAllowedDays(service);
    // Re-validate against the same holiday calendar the picker used (persisted on the
    // configured plan), not an empty set — otherwise this check and the picker disagree.
    const holidaySet = new Set(configuredPlan?.holidays ?? []);
    const minDate = getEarliestServiceDate(getLeadTime(service), allowedDays, holidaySet);
    if (!isServiceDateValid(serviceDate, minDate, allowedDays, holidaySet)) {
      invalidServiceNames.push(serviceName);
      invalidServiceDetails.push({
        name: serviceName,
        type: service.type,
        planId: service.planId,
        vendorId: service.vendorId,
        serviceDate,
        minDate,
        allowedDays: [...allowedDays],
        scheduleDiagnostics,
      });
      return;
    }

    serviceStartDateSelection[service.vendorId] = serviceDate;
  });

  if (onitSelected && onitPlan) {
    const waterService = selectedServices.find((service) => service.type === 'water');
    const waterDate = waterService ? serviceStartDateSelection[waterService.vendorId] : undefined;
    if (waterDate) {
      serviceStartDateSelection[onitPlan.vendorId] = waterDate;
    } else {
      warnings.push('ONIT selected but no water service date found; ONIT will have no start date.');
    }
  }

  return {
    serviceStartDateSelection,
    missingServiceNames,
    invalidServiceNames,
    invalidServiceDetails,
    warnings,
  };
}

export function assembleCheckoutData(input: CheckoutAssemblyInput): CheckoutAssemblyResult {
  const {
    address,
    personalInfo,
    configuredPlans,
    selectedServices,
    appFields,
    onitPlan,
    onitSelected,
    requireAppFields = false,
  } = input;

  if (!personalInfo.dob) {
    return {
      ok: false,
      error: 'Your date of birth was lost. Please go back to update your personal info.',
      logMessage: 'DOB missing at checkout',
    };
  }

  const dobCheck = validateDOB(personalInfo.dob);
  if (!dobCheck.valid) {
    return {
      ok: false,
      error: 'Your date of birth is invalid. Please go back to update your personal info.',
      logMessage: `Invalid DOB at checkout: ${personalInfo.dob} - ${dobCheck.message}`,
    };
  }

  const {
    serviceStartDateSelection,
    missingServiceNames,
    invalidServiceNames,
    invalidServiceDetails,
    warnings,
  } = buildServiceStartDateSelection(configuredPlans, selectedServices, onitPlan, onitSelected);

  if (missingServiceNames.length > 0) {
    return {
      ok: false,
      error: 'Your service start dates were lost. Please go back and re-select them.',
      logMessage: `Service dates missing for: ${missingServiceNames.join(', ')}`,
    };
  }

  if (invalidServiceNames.length > 0) {
    return {
      ok: false,
      error: 'One or more service start dates are no longer available. Please go back and re-select them.',
      logMessage: `Invalid service dates for: ${invalidServiceNames.join(', ')} | details=${JSON.stringify(invalidServiceDetails)}`,
    };
  }

  if (selectedServices.length > 0 && Object.keys(serviceStartDateSelection).length === 0) {
    return {
      ok: false,
      error: 'Your service start dates were lost. Please go back and re-select them.',
      logMessage: 'Service dates missing; configuredPlans empty or unmatched',
    };
  }

  if (requireAppFields && appFields.length === 0) {
    return {
      ok: false,
      error: 'Your form data was lost. Please go back and re-enter your information.',
      logMessage: 'App fields missing at checkout',
    };
  }

  return {
    ok: true,
    serviceStartDateSelection,
    warnings,
    checkoutData: {
      firstName: personalInfo.firstName,
      lastName: personalInfo.lastName,
      email: personalInfo.email,
      phoneNumber: personalInfo.phone,
      serviceAddressLine1: address.street,
      serviceAddressLine2: address.unit || '',
      serviceCity: address.city,
      serviceState: address.stateCode,
      serviceZip: address.zip,
      billingAddressLine1: address.street,
      billingAddressLine2: address.unit || '',
      billingCity: address.city,
      billingState: address.stateCode,
      billingZip: address.zip,
      dob: personalInfo.dob,
      buyOwn: personalInfo.rentOrOwn,
      serviceStartDateSelection,
      appFields,
      signedDate: formatSignedDate(new Date()),
    },
  };
}
